import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { loginUser, clearError } from '../store/slices/authSlice';
import { useSystemSettings, defaultSystemSettings } from '../contexts/SystemSettingsContext';
import { Eye, EyeOff, Quote } from 'lucide-react';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const { settings } = useSystemSettings();
  
  const allowRegistration = settings?.allowRegistration !== undefined ? settings.allowRegistration : defaultSystemSettings.allowRegistration;
  
  // Récupérer l'URL de redirection depuis location.state (passée par ProtectedRoute)
  const from = (location.state as any)?.from || '/';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    
    try {
      await dispatch(loginUser({ identifier: formData.identifier, password: formData.password }) as any).unwrap();
      navigate(from, { replace: true });
    } catch (error) {
      // Error is handled by Redux
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left column - Form */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 py-12 px-6 lg:px-12">
        <div className="w-full max-w-xl">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 lg:p-12">
            {/* Logo - en haut */}
            <div className="flex justify-center mb-6">
              <Link to="/" className="block">
                <img 
                  src="/logolivinglab.png" 
                  alt="E-Living Lab"
                  className="h-20 w-auto object-contain"
                />
              </Link>
            </div>

            {/* Appel au login */}
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-1">Login to your account</h1>
            {allowRegistration && (
              <p className="text-sm text-gray-500 text-center mb-6">
                Not a member yet?{' '}
                <Link to="/register" className="font-medium text-emerald-600 hover:text-emerald-500">
                  Sign Up
                </Link>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email or username
                </label>
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.identifier}
                  onChange={handleChange}
                  placeholder="your@email.com or username"
                  className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="block w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-emerald-600 hover:text-emerald-700"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember Me</span>
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-emerald-600 hover:text-emerald-500">
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  'Login To Your Account'
                )}
              </button>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs font-medium uppercase tracking-wider text-gray-500">Or login with</span>
                </div>
              </div>

              <button
                type="button"
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500/30 transition-all"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Login With Google
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Right column - Promotional */}
      <div className="hidden lg:flex flex-[1.25] min-w-[420px] max-w-[560px] bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 p-12 lg:p-14 flex-col justify-center">
        <div className="max-w-md">
          <div className="w-16 h-16 rounded-full bg-emerald-400/30 flex items-center justify-center mb-8">
            <Quote className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6">
            We're building a better supply chain platform.
          </h2>
          <div className="border-l-4 border-emerald-400/60 pl-6">
            <p className="text-emerald-50/95 text-base leading-relaxed">
              Connect with businesses, collaborate on projects, and access analytics to grow your operations. 
              E-Living Lab brings together the tools you need for a sustainable and efficient supply chain.
            </p>
          </div>
          <div className="flex items-center gap-4 mt-10">
            <div className="h-12 w-12 rounded-full bg-emerald-400/40 border-2 border-emerald-300/50 flex items-center justify-center">
              <span className="text-lg font-bold text-white">E</span>
            </div>
            <div>
              <p className="font-semibold text-white">E-Living Lab</p>
              <p className="text-sm text-emerald-100/90">Supply Chain Link</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
